/**
 * search-node.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { ServerDataType } from "../common/data/data-types";
import { TypedDataHolder } from "../common/data/typed-data";
import { SubjectContextType } from "../subjects/subject-context-type";
import { Operator, OperatorString, MagicOperator, MAGIC_FOR } from "./operators";
/**
 * The {@link SearchTerm} represents a single user-chosen "term" of a search
 * query; algebraically it's a boolean-returning expression with a 1 operator.
 */
export interface SearchTerm {
    name : string;
    operator : OperatorString;
    value : any;
    magicSource? : [MagicOperator<any>, any];
    serializedMagicSource? : [string, any];
}

/**
 * The {@link MessageSearchNode} is the format of the JSON message definition
 * for a node within the search tree.
 */
export interface MessageSearchNode extends TypedDataHolder
{
    name : string;
    displayName : string;
    createsValidSearch : boolean;
    validOperators : OperatorString[];
    keys : {searchKeys : MessageSearchNode[]; filterKeys : MessageSearchNode[]};
    type : ServerDataType;
    restrictions : any[];
}

/**
 * The {@link MessageSearchTree} is the root-level defintion in the message JSON
 * for the forest of possible search queries and the type that forest is
 * associated with.
 */
export interface MessageSearchTree
{
    contextType : SubjectContextType;
    tree : MessageSearchNode[];
}

/**
 * The {@link SearchNode} is the superclass of nodes within a search tree,
 * defining
 */
export class SearchNode implements TypedDataHolder
{
    constructor (
        public name : string,
        data? : MessageSearchNode
    )
    {
        if (data)
        {
            this.type = data.type;
            this.restrictions = data.restrictions || [];
            this.displayName = data.displayName || "";
            // We immediately filter out operators that the client does not
            // allow for this data type. This is easier than waiting until the
            // view and ensures "illegal" operators aren't seen beyond messages.
            this.operators = (data.validOperators || []).filter(
                (op : OperatorString) => Operator.supports(op, data));
            const addlMagicOps : MagicOperator<any>[] =
              MAGIC_FOR.get(data.type) || [];
            addlMagicOps.forEach(
              (a : MagicOperator<any>) => this.operators.push(a));
            this.createsValidSearch = data.createsValidSearch;
        }
    }

    public parent : SearchNode = null;
    public children : SearchNode[] = [];
    public displayName : string = "";
    public operators : Array<OperatorString | MagicOperator<any>> = [];
    public createsValidSearch : boolean = false;

    public type : ServerDataType = "string";
    public restrictions? : any[];
    public readonly cardinality : number = 1;
    public readonly value : any = null;

    public retrieve (path : string[]) : SearchNode
    {
        if (path.length === 0)
        {
            return this;
        }
        const child : SearchNode = this.children.find((node : SearchNode) =>
            node.name === path[0]);
        if (child instanceof SearchNode)
        {
            return child.retrieve(path.slice(1));
        }
        return null;
    }

    public addChild (child : SearchNode) : void
    {
        child.parent = this;
        this.children.push(child);
    }

    public get filters () : FilterNode[]
    {
        return this.children.filter(
            (n : SearchNode) => n instanceof FilterNode);
    }
    public get paritions () : PartitionNode[]
    {
        return this.children.filter(
            (n : SearchNode) => n instanceof PartitionNode);
    }
    public canSearchHere () : boolean
    {
        return this.createsValidSearch;
    }

    public generateFlatOptions (ancestors : SearchNode[])
      : SearchNode[][]
    {
      let result : SearchNode[][] = [];
      const toHere : SearchNode[] = ancestors.slice(0).concat([this]);
      if (this.canSearchHere())
      {
        result.push(toHere);
      }
      this.children.sort(compareNodeNames).forEach((n : SearchNode) => {
        result = result.concat(n.generateFlatOptions(toHere));
      });
      return result;
    }
}

function compareNodeNames (a : SearchNode, b : SearchNode) : number
{
  return a.name.localeCompare(b.name);
}

const FIXED_NAMES : Map<string, number> = new Map([
    ["name short3x3", -50],
    ["last name", -25],
    ["first name", -20]
]);

function nameValue (name : string) : number
{
    const lc : string = name.toLocaleLowerCase();
    if (FIXED_NAMES.has(lc))
    {
        return FIXED_NAMES.get(lc);
    }
    if (lc.indexOf("ir ") === 0)
    {
        return 10;
    }
    return 0;
}

export class SearchRoot extends SearchNode
{
    constructor ()
    {
        super(`«root»`, null);
    }

    public static fromMessage (msgNode : MessageSearchNode[]) : SearchNode
    {
        const root : SearchNode = new SearchRoot();
        msgNode.forEach((n : MessageSearchNode) =>
            root.addChild(PartitionNode.fromMessage(n)));
        root.children.sort((a : SearchNode, b : SearchNode) =>
            nameValue(a.name) - nameValue(b.name));
        return root;
    }

    public static readonly EMPTY : SearchRoot = new SearchRoot();
}
export class PartitionNode extends SearchNode
{
    public static fromMessage (msgNode : MessageSearchNode) : SearchNode
    {
        const node : SearchNode = new PartitionNode(msgNode.name, msgNode);
        msgNode.keys.filterKeys.forEach((n : MessageSearchNode) =>
            node.addChild(FilterNode.fromMessage(n)));
        msgNode.keys.searchKeys.forEach((n : MessageSearchNode) =>
            node.addChild(PartitionNode.fromMessage(n)));
        return node;
    }
}
export class FilterNode extends SearchNode
{
    public static fromMessage (msgNode : MessageSearchNode) : SearchNode
    {
        const node : SearchNode = new FilterNode(msgNode.name, msgNode);
        msgNode.keys.filterKeys.forEach((n : MessageSearchNode) =>
            node.addChild(FilterNode.fromMessage(n)));
        return node;
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/search/search-node.ts